import { withDevtools } from '@ngrx-toolkit/core';
import { computed, effect, inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { AuthStore } from '@mb/auth/client';
import { clientLogger, isBrowser } from '@mb/common/client';
import {
  maxScope,
  scopeAtLeast,
  scopeKey,
  type PermissionAction,
  type PermissionScope,
  type ScopeMap,
} from '@mb/auth/shared';
import type { OrganizationContextDto, OrganizationSummary } from '../../shared';
import { OrganizationContextResourceService } from '../services/organization-context-resource.service';

/**
 * OrganizationContextStore — the client's source of truth for *which organization
 * the user is acting in* and *what they may do there*.
 *
 * Context reads are driven by {@link OrganizationContextResourceService} (`httpResource`).
 * Normally hydrated synchronously from the dashboard SSR load via
 * {@link hydrateFromServer}; the AuthStore-driven effect is a client-side fallback.
 */

const SELECTED_ORG_COOKIE = 'mb_selected_org';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 1 year

function persistOrgId(id: string | null): void {
  if (!isBrowser()) return;
  try {
    document.cookie = id
      ? `${SELECTED_ORG_COOKIE}=${encodeURIComponent(id)}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax`
      : `${SELECTED_ORG_COOKIE}=; path=/; max-age=0; SameSite=Lax`;
  } catch {
    /* document.cookie unavailable — ignore */
  }
}

interface OrganizationContextState {
  organizations: OrganizationSummary[];
  selectedOrganizationId: string | null;
  /** `${entity}.${action}` → the set of scopes the user holds in the active org. */
  scopes: ScopeMap;
  /** True once context resolution has settled (loaded, failed, or no session). */
  isInitialized: boolean;
  error: string | null;
}

const initialState: OrganizationContextState = {
  organizations: [],
  selectedOrganizationId: null,
  scopes: {},
  isInitialized: false,
  error: null,
};

export const OrganizationContextStore = signalStore(
  withDevtools('organization context store'),
  withState(initialState),
  withComputed((state) => {
    const contextResource = inject(
      OrganizationContextResourceService,
    ).contextResource;
    const authStore = inject(AuthStore);

    return {
      selectedOrganization: computed(
        () =>
          state
            .organizations()
            .find((o) => o.id === state.selectedOrganizationId()) ?? null,
      ),
      hasOrganizations: computed(() => state.organizations().length > 0),
      isLoading: computed(() => contextResource.isLoading()),
      queryableOrganizationId: computed(() => {
        if (
          !isBrowser() ||
          !authStore.isInitialized() ||
          !authStore.isAuthenticated()
        ) {
          return undefined;
        }
        return state.selectedOrganizationId() ?? undefined;
      }),
    };
  }),
  withMethods((store) => {
    const contextReads = inject(OrganizationContextResourceService);

    return {
      scopeFor: (
        entity: string,
        action: PermissionAction,
      ): PermissionScope | null => {
        const have = store.scopes()[scopeKey(entity, action)] ?? [];
        return have.length ? have.reduce((a, b) => maxScope(a, b)) : null;
      },

      can: (
        entity: string,
        action: PermissionAction,
        minScope?: PermissionScope,
      ): boolean => {
        const have = store.scopes()[scopeKey(entity, action)] ?? [];
        if (!have.length) return false;
        return minScope ? have.some((s) => scopeAtLeast(s, minScope)) : true;
      },

      hydrateFromServer: (dto: OrganizationContextDto): void => {
        contextReads.clear();
        patchState(store, {
          organizations: dto.organizations,
          selectedOrganizationId: dto.selectedOrganizationId,
          scopes: dto.scopes,
          isInitialized: true,
          error: null,
        });
        persistOrgId(dto.selectedOrganizationId);
      },

      load: (): void => {
        if (store.isLoading() || store.isInitialized()) {
          return;
        }
        patchState(store, { error: null });
        contextReads.requestDefaultLoad();
      },

      selectOrganization: (organizationId: string): void => {
        if (organizationId === store.selectedOrganizationId()) {
          return;
        }
        patchState(store, { error: null });
        contextReads.requestOrganization(organizationId);
      },

      clear: (): void => {
        contextReads.clear();
        patchState(store, initialState);
        persistOrgId(null);
      },
    };
  }),
  withHooks({
    onInit(store) {
      const authStore = inject(AuthStore);
      const contextReads = inject(OrganizationContextResourceService);

      effect(() => {
        const status = contextReads.contextResource.status();
        const params = contextReads.fetchParams();

        if (status === 'resolved' && params !== null) {
          const dto = contextReads.contextResource.value();
          if (!dto) {
            return;
          }
          patchState(store, {
            organizations: dto.organizations,
            selectedOrganizationId: dto.selectedOrganizationId,
            scopes: dto.scopes,
            isInitialized: true,
            error: null,
          });
          persistOrgId(dto.selectedOrganizationId);
          return;
        }

        if (status === 'error' && params !== null) {
          clientLogger.error('[ORG CONTEXT] Failed to load context', {
            error:
              contextReads.contextResource.error() ??
              new Error('Context fetch failed'),
          });
          patchState(store, {
            error:
              params.purpose === 'switch'
                ? 'organization.context.switchFailed'
                : 'organization.context.loadFailed',
            isInitialized: true,
          });
        }
      });

      effect(() => {
        if (!isBrowser()) return;
        if (!authStore.isInitialized()) return;

        const authenticated = authStore.isAuthenticated();
        if (authenticated && !store.isInitialized() && !store.isLoading()) {
          store.load();
        } else if (!authenticated && store.isInitialized()) {
          store.clear();
        }
      });
    },
  }),
);
